/**
 * Auth context and provider.
 *
 * Responsibilities:
 *   * Initialize Keycloak once on mount.
 *   * Expose a typed `useAuth()` hook that returns the current profile,
 *     a live access token, and `login` / `logout` actions.
 *   * Refresh the access token ~30s before expiry. If refresh fails the
 *     user is bounced back to Keycloak.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { DEFAULT_INIT, getKeycloak, profileFromToken, type TokenProfile } from "./keycloak";

export type AuthState =
  | { status: "initializing" }
  | { status: "anonymous" }
  | { status: "authenticated"; profile: TokenProfile; token: string };

type AuthContextValue = AuthState & {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Trigger Keycloak's hardened password-change flow.
   *
   * Internally this is `kc.login({ action: "UPDATE_PASSWORD" })`, which
   * sends the user back through the IdP to Keycloak's built-in
   * Update Password form. Once the user picks a new password they are
   * redirected back to `redirectUri` (defaults to the current href) with
   * a fresh session — no in-app password form is ever rendered, so the
   * SPA never holds the user's plaintext password and complexity / breach
   * / brute-force protections all stay on the IdP where they belong.
   */
  changePassword: () => Promise<void>;
  getToken: () => string | undefined;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "initializing" });
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const kc = getKeycloak();

    kc.onTokenExpired = () => {
      kc.updateToken(30)
        .then((refreshed) => {
          if (refreshed && kc.token) {
            setState({
              status: "authenticated",
              profile: profileFromToken(kc),
              token: kc.token,
            });
          }
        })
        .catch(() => {
          // Refresh failed -> drop to anonymous and let the user re-auth
          // explicitly rather than ping-ponging through Keycloak.
          setState({ status: "anonymous" });
        });
    };

    kc.init(DEFAULT_INIT)
      .then((authenticated) => {
        if (authenticated && kc.token) {
          setState({
            status: "authenticated",
            profile: profileFromToken(kc),
            token: kc.token,
          });
        } else {
          setState({ status: "anonymous" });
        }
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error("keycloak init failed", err);
        setState({ status: "anonymous" });
      });
  }, []);

  const login = useCallback(async () => {
    await getKeycloak().login({ redirectUri: window.location.href });
  }, []);

  const logout = useCallback(async () => {
    await getKeycloak().logout({ redirectUri: window.location.origin });
  }, []);

  const changePassword = useCallback(async () => {
    await getKeycloak().login({
      action: "UPDATE_PASSWORD",
      redirectUri: window.location.href,
    });
  }, []);

  const getToken = useCallback(() => getKeycloak().token, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, changePassword, getToken }),
    [state, login, logout, changePassword, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
